<template>
  <div class="form-container">
    <vxe-form v-bind="formOptions" v-on="formEvents"></vxe-form>
  </div>
</template>
<script setup>
import { reactive, defineProps, onMounted, defineEmits, computed } from 'vue'
import { VxeUI } from 'vxe-pc-ui'
import convertToFirstLetter from '@/utils/pingyingHelper'
import request from '@/request/request'
import showMessage from '@/utils/showmessage'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ usefule: '新增', Product: {} })
  }
})
const emit = defineEmits(['submit'])
const isEdit = computed(() => props.initialData?.usefule === '修改')

// ==================== 表单数据 ====================
const formData = reactive({
  ID: 0,
  SP_BH: '',   // 编号
  SP_DW: '',   // 单位
  SP_GG: '',   // 规格
  SP_MC: '',   // 名称
  SP_XH: '',   // 型号
  SP_ZJC: '',  // 助记词
  SP_QM: '',   // 全名
  SP_KC: 0     // 库存
})

// ==================== 工具：前端大写 key → 后端首字母小写 key ====================
// SP_DW → sP_DW ; SP_MC → sP_MC ; ID → iD
function toPropKey(key) {
  return key.charAt(0).toLowerCase() + key.slice(1)
}

// ==================== 自动生成助记词 SP_ZJC ====================
// 规则：名称 + 型号 + 规格 的拼音首字母
function updateZJC() {
  const src = (formData.SP_MC || '') + (formData.SP_XH || '') + (formData.SP_GG || '')
  formData.SP_ZJC = convertToFirstLetter(src)
  formData.SP_QM = (formData.SP_GG || '') +' '+ (formData.SP_MC || '') +' '+ (formData.SP_XH || '')
}

// ==================== 从 props 加载数据 ====================
onMounted(() => {
  if (props.initialData.usefule === '修改') {
    const product = props.initialData.Product || {}
    console.log('原始 product:', product)

    Object.keys(formData).forEach(key => {
      const propKey = toPropKey(key)         // SP_DW → sP_DW
      if (product[propKey] !== undefined) {
        formData[key] = product[propKey]
      }
    })
  }
})
// 建议放在 script 顶部或单独常量文件
const UNIT_OPTIONS = [
  { label: '台',  value: '台'  },
  { label: '套',  value: '套'  },
  { label: '个',  value: '个'  },
  { label: '只',  value: '只'  },
  { label: '条',  value: '条'  },
  { label: '根',  value: '根'  },
  { label: '块',  value: '块'  },
  { label: '张',  value: '张'  },
  { label: '盒',  value: '盒'  },
  { label: '包',  value: '包'  },
  { label: '支',  value: '支'  },
  { label: '瓶',  value: '瓶'  },
  { label: '卷',  value: '卷'  },
  { label: '米',  value: '米'  },
  { label: '箱', value: '箱' },
  { label: '份',  value: '份'  }
]
// ==================== 表单配置 ====================
const formOptions = reactive({
  titleWidth: 100,
  data: formData,

  // ⭐ rules：除 ID、SP_BH 外全部必填
  rules: {
    SP_DW:  [{ required: true, message: '请输入单位' }],
    SP_GG:  [{ required: true, message: '请输入规格' }],
    SP_MC:  [{ required: true, message: '请输入名称' }],
    SP_XH:  [{ required: true, message: '请输入型号' }],
    SP_ZJC: [{ required: true, message: '请输入助记词' }],
    SP_QM:  [{ required: true, message: '请输入全名' }],
    SP_KC:  [{ required: true, message: '请输入库存' }]
  },

  items: [
   
    {
      field: 'SP_GG', title: '商品大类', span: 12,
      itemRender: {
        name: 'VxeInput',
        props: { placeholder: '请输入商品大类' },
        events: {
          input: (_, { value }) => {
            formData.SP_GG = value
            updateZJC()
          }
        }
      }
    },
    {
      field: 'SP_MC', title: '品牌', span: 12,
      itemRender: {
        name: 'VxeInput',
        props: { placeholder: '请输入品牌' },
        events: {
          input: (_, { value }) => {
            formData.SP_MC = value
            updateZJC()
          }
        }
      }
    },
    {
      field: 'SP_XH', title: '型号', span: 12,
      itemRender: {
        name: 'VxeInput',
        props: { placeholder: '请输入规格型号' },
        events: {
          input: (_, { value }) => {
            formData.SP_XH = value
            updateZJC()
          }
        }
      }
    },
    {
  field: 'SP_DW', title: '单位', span: 12,
  itemRender: {
    name: 'VxeSelect',
    props: {
      placeholder: '请选择单位',
      clearable: true,          // 允许清空
      filterable: true,         // ⭐ 支持搜索，选项多时必开
      options: UNIT_OPTIONS
    }
  }
},
    {
      field: 'SP_QM', title: '全名', span: 12,
      itemRender: {
        name: 'VxeInput',
        props: { placeholder: '请输入全名',disabled: true }   
      }
    },
    {
      field: 'SP_ZJC', title: '助记词', span: 12,
      itemRender: {
        name: 'VxeInput',
        props: { placeholder: '自动生成（名称+型号+规格）' }
      }
    },
    {
      field: 'SP_KC', title: '库存', span: 12,
      itemRender: {
        name: 'VxeNumberInput',
        props: {
          placeholder: '请输入库存',
          digits: 0,
          disabled: isEdit.value   // 修改时禁用
        }
      }
    },
    // 按钮组
    {
      align: 'center', span: 24,
      itemRender: {
        name: 'VxeButtonGroup',
        options: [
          { type: 'submit', content: '提交', status: 'primary' },
          { type: 'reset', content: '重置' }
        ]
      }
    }
  ]
})

// ==================== 提交 / 重置 ====================
const formEvents = {
  async submit() {
    const url = props.initialData.usefule === '新增'
      ? '/Product/AddNew'
      : '/Product/Update'
    const res = await request.post(url, formData)
    if (showMessage(res)) emit('submit')
  },
  reset() {
    VxeUI.modal.message({ content: '已重置', status: 'info' })
  }
}
</script>